import isUndefined from 'lodash-es/isUndefined'
import range from 'lodash-es/range'
import { reverse } from 'named-urls'
import type { ReactNode } from 'react'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { ProductItemGroupedDetails } from '@components/common/ProductItemGroupedDetails/ProductItemGroupedDetails'
import { SideSummary } from '@components/common/SideSummary'
import {
  BackButton,
  Button,
  Checkbox,
  Division,
  HeadContent,
  Heading,
  Loading,
  PriceString,
  ProductsList,
  Select,
  Summary,
} from '@fourthwall/components'
import type { Price } from '@fourthwall/services/dist/models/common'
import type { NonCancellableOrderOffer } from '@fourthwall/services/dist/models/order'
import { routing } from '@utils/routing'

import type { Order } from '../../Order.types'
import { getNonCancellableReason } from '../../utils/getNonCancellableReason'
import type { SelectableOffer } from './OrderCancel.types'
import * as S from './OrderCancelView.styled'

export type OrderCancelViewProps = {
  orderId: string
  friendlyId?: string
  cancelableOffers?: SelectableOffer[]
  nonCancellableOffers?: NonCancellableOrderOffer[]
  promotion?: Order['promotion']
  donation: Price | undefined
  subtotal?: Price
  shipping?: Price
  taxes?: Price
  discount?: Price
  refundTotal?: Price
  shouldRestock: boolean
  isCancelButtonDisabled: boolean
  isLoading: boolean
  onOrderCancel: (productsContent: ReactNode) => void
  onOfferSelect: (offer: SelectableOffer, selected: boolean) => void
  onRestockChange: (value: boolean) => void
  onOfferSelectedQuantityChange: (offer: SelectableOffer, quantity: number) => void
  onSelectAll: (selected: boolean) => void
}

export const OrderCancelView = ({
  orderId,
  friendlyId = '',
  cancelableOffers,
  nonCancellableOffers,
  promotion,
  donation,
  subtotal,
  shipping,
  taxes,
  discount,
  refundTotal,
  isCancelButtonDisabled,
  shouldRestock,
  isLoading,
  onOrderCancel,
  onOfferSelect,
  onRestockChange,
  onOfferSelectedQuantityChange,
  onSelectAll,
}: OrderCancelViewProps) => {
  const selectedOffers = cancelableOffers?.filter((offer) => offer.selected)
  const selectedOffersCount = selectedOffers?.reduce(
    (prev, selectedOffer) => prev + selectedOffer.selectedQuantity,
    0,
  )
  const areAllOffersSelected = selectedOffers?.length === cancelableOffers?.length

  const getSelectedQuantityOptions = (quantity: number) => {
    return range(1, quantity + 1).map((number) => ({
      label: number.toString(),
      value: number.toString(),
    }))
  }

  const renderPromotionDescription = () => {
    if (promotion?.type.type === 'FixedAmount') {
      return (
        <>
          <PriceString value={promotion.type.money.value} /> off
        </>
      )
    }

    if (promotion?.type.type === 'Percentage') {
      return `${promotion.type.percentage}% off entire order`
    }

    if (promotion?.type.type === 'FreeShipping') {
      return 'Free shipping'
    }

    return ''
  }

  const summaryContent = (
    <>
      {!isUndefined(subtotal) && selectedOffers && (
        <Summary.Row label={`Subtotal (${selectedOffersCount} items)`} value={subtotal} />
      )}
      {!isUndefined(shipping) && <Summary.Row label="Shipping" value={shipping} />}
      {!isUndefined(taxes) && taxes?.value > 0 && <Summary.Row label="Sales tax" value={taxes} />}
      {!isUndefined(discount) && discount?.value > 0 && (
        <>
          <Summary.Row
            label="Discount"
            value={{
              value: discount.value * -1,
              currency: discount.currency,
            }}
          />
          <S.SummarySubsection>
            {promotion && <div>{promotion.isAutoApplied ? promotion.title : promotion.code}</div>}
            {promotion && <div>{renderPromotionDescription()}</div>}
          </S.SummarySubsection>
        </>
      )}

      {!isUndefined(refundTotal) && (
        <>
          <S.SummaryDivider />
          <Summary.Row label="Refund amount" value={refundTotal} bold />
        </>
      )}
    </>
  )

  const renderOffer = (
    orderOffer: SelectableOffer | NonCancellableOrderOffer,
    editable: boolean,
  ) => {
    const { variant, offer, quantity, price, grouped } = orderOffer

    const isSelectable = (o: SelectableOffer | NonCancellableOrderOffer): o is SelectableOffer => {
      return (o as SelectableOffer).selected !== undefined
    }

    return (
      <Division size="small" key={offer.id}>
        <S.ProductItemContainer data-testid={`OrderCancelView.OfferSection-${offer.id}`}>
          {editable && isSelectable(orderOffer) && (
            <S.ProductCheckoutContainer>
              <Checkbox
                value={orderOffer.selected}
                onValueChange={(value) => onOfferSelect(orderOffer, value)}
              />
            </S.ProductCheckoutContainer>
          )}
          <S.ProductDetailsContainer>
            <ProductsList.Item
              name={offer.name}
              description={variant.attributesDescription}
              details={grouped ? <ProductItemGroupedDetails groupedDetails={grouped} /> : undefined}
              tag={
                !isSelectable(orderOffer)
                  ? getNonCancellableReason(orderOffer.nonCancellableReason)
                  : undefined
              }
              image={offer.thumbnailImage}
              type={offer.offerType === 'GIFT_CARD' ? 'giftCard' : undefined}
            />
          </S.ProductDetailsContainer>
          <S.ProductQuantityContainer>
            {editable && isSelectable(orderOffer) && quantity > 1 && (
              <Select
                size="small"
                label="Quantity"
                options={getSelectedQuantityOptions(quantity)}
                value={orderOffer.selectedQuantity.toString()}
                onValueChange={(value) =>
                  onOfferSelectedQuantityChange(orderOffer, parseInt(value))
                }
              />
            )}
            {isSelectable(orderOffer) && (quantity <= 1 || !editable) && (
              <S.ProductQuantityText>{orderOffer.selectedQuantity}</S.ProductQuantityText>
            )}
            {!isSelectable(orderOffer) && (
              <S.ProductQuantityText>{orderOffer.quantity}</S.ProductQuantityText>
            )}
          </S.ProductQuantityContainer>
          <S.ProductPriceContainer>
            <PriceString value={price.value} />
          </S.ProductPriceContainer>
        </S.ProductItemContainer>
      </Division>
    )
  }

  const renderDonation = () => (
    <Division size="small">
      <S.ProductItemContainer>
        <S.ProductDetailsContainer>
          <ProductsList.Item name="Donation" type="donation" />
        </S.ProductDetailsContainer>
        <S.ProductQuantityContainer>
          <S.ProductQuantityText>1</S.ProductQuantityText>
        </S.ProductQuantityContainer>
        <S.ProductPriceContainer>
          <PriceString {...donation} />
        </S.ProductPriceContainer>
      </S.ProductItemContainer>
    </Division>
  )

  const renderOffers = (
    offers: SelectableOffer[] | NonCancellableOrderOffer[],
    editable: boolean,
  ) => {
    return offers?.map((offer) => renderOffer(offer, editable))
  }
  const handleCancelOrderClick = () => {
    if (!selectedOffers?.length) {
      return
    }

    const productsContent = (
      <>
        <Division size="small" />
        <Division size="small">
          <ProductsList>{renderOffers(selectedOffers, false)}</ProductsList>
        </Division>
        <Division size="small">
          <Summary>{summaryContent}</Summary>
        </Division>
      </>
    )

    onOrderCancel(productsContent)
  }

  const leftContent = (
    <>
      {cancelableOffers && cancelableOffers.length > 0 && (
        <S.Section>
          <S.SectionHeader>
            <Checkbox
              value={areAllOffersSelected}
              onValueChange={(value) => onSelectAll(value)}
              label={areAllOffersSelected ? 'Unselect all' : 'Select all'}
            />
          </S.SectionHeader>
          <Division size="small">
            <ProductsList>{renderOffers(cancelableOffers, true)}</ProductsList>
          </Division>
          <Division size="small" />
        </S.Section>
      )}
      {(donation || (nonCancellableOffers && nonCancellableOffers.length > 0)) && (
        <S.Section>
          <S.SectionHeader>
            <Heading type="h5" weight="semibold">
              Items that cannot be canceled
            </Heading>
          </S.SectionHeader>
          <Division size="small">
            <ProductsList>
              {nonCancellableOffers && nonCancellableOffers.length > 0 && (
                <>{renderOffers(nonCancellableOffers, false)}</>
              )}
              {donation && renderDonation()}
            </ProductsList>
          </Division>
          <Division size="small" />
        </S.Section>
      )}
    </>
  )

  const summary = (
    <SideSummary>
      <S.SummaryHeader>Summary</S.SummaryHeader>
      <Summary small>{summaryContent}</Summary>
      <S.SummaryButton>
        <Button
          label="Cancel items"
          appearance="primary"
          fullWidth
          disabled={isCancelButtonDisabled}
          onClick={handleCancelOrderClick}
        />
      </S.SummaryButton>
      <S.SummaryCheckbox>
        <Checkbox value={shouldRestock} label="Restock items" onValueChange={onRestockChange} />
      </S.SummaryCheckbox>
    </SideSummary>
  )

  return (
    <>
      <HeadContent title={`Cancel items (#${friendlyId})`} />
      <Loading isLoading={isLoading}>
        <PageLayout leftContent={leftContent} rightContent={summary}>
          <Dashboard.ItemViewHeader
            name="Cancel items"
            backButton={
              <BackButton
                label={`Order #${friendlyId}`}
                to={{ pathname: reverse(routing.contributions.orders.order.self, { orderId }) }}
              />
            }
          />
        </PageLayout>
      </Loading>
    </>
  )
}
